import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { LangCode } from "@/lib/types";
import { useLocation } from "@tanstack/react-router";
import { activeDict, hydrateFullDict } from "@/lib/i18n-dict";
import { VALID_LANGS } from "@/lib/utils/locale";

export interface LanguageInfo {
  code: LangCode;
  label: string;
  nativeName: string;
  dir: "ltr" | "rtl";
}

/**
 * No flag field: the switcher used emoji flags, and emoji are not part of this
 * site's icon system (lucide throughout). Country flags are also the wrong
 * signifier for a language — the UK flag excluded every other English market,
 * and the Saudi flag stood in for Arabic across Egypt, Morocco and the Gulf
 * alike.
 *
 * The switcher now renders the ISO code plus `nativeName`, which is what a
 * speaker actually recognises. If flags are ever wanted back they must be SVG
 * assets rather than emoji — that path is also the one Persian needs, since
 * Unicode has no Lion and Sun code point (see docs/project/plans/VARS-001-persian-locale.md).
 */
export const LANGUAGES: LanguageInfo[] = [
  { code: "en", label: "EN", nativeName: "English", dir: "ltr" },
  { code: "tr", label: "TR", nativeName: "Türkçe", dir: "ltr" },
  { code: "ar", label: "AR", nativeName: "العربية", dir: "rtl" },
  { code: "de", label: "DE", nativeName: "Deutsch", dir: "ltr" },
  { code: "ru", label: "RU", nativeName: "Русский", dir: "ltr" },
  { code: "ja", label: "JA", nativeName: "日本語", dir: "ltr" },
  { code: "ko", label: "KO", nativeName: "한국어", dir: "ltr" },
  { code: "zh", label: "ZH", nativeName: "简体中文", dir: "ltr" },
  { code: "es", label: "ES", nativeName: "Español", dir: "ltr" },
  // Second RTL locale after Arabic. Technical values keep Western digits —
  // a spec sheet is read comparatively against suppliers quoting in them.
  { code: "fa", label: "FA", nativeName: "فارسی", dir: "rtl" },
  // Eleventh locale. Serves France and Belgium plus the Maghreb, where
  // Mediterranean aquaculture and seafood trade are conducted in French.
  { code: "fr", label: "FR", nativeName: "Français", dir: "ltr" },
];

/**
 * Locale codes only. The dictionaries themselves are no longer imported here:
 * doing so pulled all eleven (~1.7 MB) into the shared client chunk. The active
 * one is resolved per request on the server and inlined into the HTML — see
 * src/lib/i18n-dict.ts.
 */
const DICTS: Record<LangCode, true> = {
  en: true,
  tr: true,
  ar: true,
  ru: true,
  de: true,
  ja: true,
  ko: true,
  zh: true,
  es: true,
  fa: true,
  fr: true,
};

interface I18nContextValue {
  lang: LangCode;
  dir: "ltr" | "rtl";
  setLang: (l: LangCode) => void;
  t: (key: string) => string;
  languages: LanguageInfo[];
  currentLanguage: LanguageInfo;
}

const I18nContext = createContext<I18nContextValue | null>(null);
const STORAGE_KEY = "vars.lang";

export function detectLangFromUrl(
  pathname: string,
  search: string | Record<string, unknown>,
): LangCode {
  let qLang: LangCode | null = null;
  if (typeof search === "string") {
    const params = new URLSearchParams(search);
    qLang = params.get("lang") as LangCode | null;
  } else if (search && typeof search === "object") {
    qLang = (search.lang as LangCode) || null;
  }

  if (qLang && DICTS[qLang]) return qLang;

  const pathParts = pathname.split("/").filter(Boolean);
  if (pathParts.length > 0) {
    const maybeLang = pathParts[0].toLowerCase() as LangCode;
    if (DICTS[maybeLang]) return maybeLang;
  }
  return "en";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const location = useLocation();

  // Initialize state based strictly on the URL (and server storage on SSR) to match server-side render exactly!
  const [lang, setLangState] = useState<LangCode>(() => {
    if (typeof window === "undefined") {
      const storage = (globalThis as unknown as Record<string, unknown>).serverStorage as
        { getStore: () => { lang?: string } | undefined } | undefined;
      const store = storage?.getStore();
      if (store?.lang) return store.lang as LangCode;
      return "en";
    }
    return detectLangFromUrl(window.location.pathname, window.location.search);
  });

  // Keep state in sync with route location changes reactively (e.g. when clicking regular links)
  useEffect(() => {
    const currentPath =
      typeof window !== "undefined" ? window.location.pathname : location.pathname;
    const currentSearch = typeof window !== "undefined" ? window.location.search : location.search;
    const urlLang = detectLangFromUrl(currentPath, currentSearch);
    if (urlLang !== lang) {
      setLangState(urlLang);
    }
  }, [location.pathname, location.search, lang]);

  useEffect(() => {
    const currentInfo = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
    const dir = currentInfo.dir;

    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("dir", dir);
      document.documentElement.setAttribute("lang", lang);
    }
  }, [lang]);

  // No post-hydration locale redirect.
  //
  // This used to read localStorage and navigator.language on mount and bounce
  // the user to another locale. Two problems: redirecting on browser language
  // alone is contrary to Google's guidance and left an English speaker with a
  // non-English browser unable to reach the English pages; and since the
  // dictionary now arrives with the document, the bounce cost a second full
  // page load. Locale preference is resolved server-side from the vars.lang
  // cookie instead — see src/server.ts.

  const setLang = (l: LangCode) => {
    if (!DICTS[l]) return;
    setLangState(l);
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(STORAGE_KEY, l);
        // Also a cookie: the server reads this to serve the preferred locale
        // on the first request, which avoids the post-hydration redirect and
        // the extra full page load it caused.
        document.cookie = `${STORAGE_KEY}=${l}; path=/; max-age=31536000; samesite=lax`;
      } catch {
        /* ignore */
      }

      // Sync URL prefix
      const currentPath = window.location.pathname;
      const parts = currentPath.split("/").filter(Boolean);
      let restOfPath = "";
      if (parts.length > 0) {
        const firstPart = parts[0].toLowerCase();
        if (VALID_LANGS.includes(firstPart as LangCode)) {
          restOfPath = "/" + parts.slice(1).join("/");
        } else {
          restOfPath = "/" + parts.join("/");
        }
      } else {
        restOfPath = "/";
      }

      const prefix = l === "en" ? "" : `/${l}`;
      const newPath = `${prefix}${restOfPath === "/" ? "" : restOfPath}` || "/";

      if (window.location.pathname !== newPath) {
        // See the note above: switching locale must re-request the page so the
        // server inlines the target locale's dictionary.
        window.location.assign(newPath);
      }
    }
  };

  // The document inlines only the slice this page rendered plus the shared
  // chrome, so the rest of the dictionary is fetched once after hydration and
  // merged in.
  //
  // Fetch and merge only — no forced re-render, and that is the interesting
  // part. An earlier version bumped a state counter when the dictionary landed,
  // so anything already showing a fallback would correct itself. It also
  // re-rendered the entire tree at an arbitrary moment, and
  // `rtl.spec.ts:45` caught what that costs: the merge landing mid-interaction
  // reset Radix's roving tabindex, so ArrowLeft stopped moving between product
  // tabs in Arabic. Reproducible at two workers, green in isolation — a real
  // interaction bug, not a flaky test.
  //
  // Nothing needs the re-render. `t()` reads the global on every call, so any
  // later render is already correct, and everything rendered during SSR is in
  // the inlined slice by construction. The only exposure is a component that
  // rendered a fallback in the brief window before the merge and then never
  // re-renders for any other reason — rare, self-correcting, and far cheaper
  // than resetting focus under someone's hands.
  useEffect(() => {
    let idleId: number | undefined;
    let timerId: number | undefined;
    const trigger = () => {
      void hydrateFullDict();
    };
    const onFirstInteraction = () => {
      cleanup();
      trigger();
    };
    const cleanup = () => {
      window.removeEventListener("pointerdown", onFirstInteraction);
      window.removeEventListener("keydown", onFirstInteraction);
      if (idleId !== undefined && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      if (timerId !== undefined) {
        clearTimeout(timerId);
      }
    };
    window.addEventListener("pointerdown", onFirstInteraction, { once: true, passive: true });
    window.addEventListener("keydown", onFirstInteraction, { once: true, passive: true });
    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(
        () => {
          cleanup();
          trigger();
        },
        { timeout: 3000 },
      );
    } else {
      timerId = window.setTimeout(() => {
        cleanup();
        trigger();
      }, 2000);
    }
    return cleanup;
  }, []);

  // activeDict() is pre-merged over English server-side, so a key missing from
  // the target locale already carries the English string — same result as the
  // previous DICTS[lang] ?? DICTS.en chain, without shipping nine dictionaries.
  const t = (key: string): string => activeDict()[key] ?? key;

  const currentLanguage = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  return (
    <I18nContext.Provider
      value={{
        lang,
        dir: currentLanguage.dir,
        setLang,
        t,
        languages: LANGUAGES,
        currentLanguage,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
